import { useSessionQuery } from '@/useQuery/queries/session';
import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { SessionResponse } from '@/types/api/api-v2';
import { setCookie } from 'cookies-next';
import cookiesConstants from '@/constants/cookies.constants';

type SessionContextType = SessionResponse | { sessionToken: string | null } | null;

// Create a default context value
const defaultContextValue = { sessionToken: null };

const SessionContext = createContext<SessionContextType>(defaultContextValue);

interface SessionProviderProps {
  children: ReactNode;
  sessionToken: string | null;
}

export const SessionProvider = ({ children, sessionToken }: SessionProviderProps) => {
  // Use a state to track initialization
  const [isInitialized, setIsInitialized] = useState(false);
  const { data: session, isLoading } = useSessionQuery(sessionToken);

  useEffect(() => {
    // Mark as initialized after the first render
    setIsInitialized(true);

    if (session) {
      if (session.cookies && session.cookies.length > 0) {
        session.cookies.forEach(cookie => {
          if (cookie.startsWith('sessionToken=')) {
            // Extract the token value from the cookie string
            const tokenValue = cookie.split('=')[1].split(';')[0];

            // Parse other cookie properties like path, expires, etc.
            const cookieOptions: { [key: string]: any } = {};

            const cookieParts = cookie.split(';');
            for (let i = 1; i < cookieParts.length; i++) {
              const part = cookieParts[`${i}`].trim();
              if (part.includes('=')) {
                const [key, value] = part.split('=');
                const keyLower = key.toLowerCase();

                // Handle expires properly - convert to Date object
                if (keyLower === 'expires') {
                  try {
                    cookieOptions[`${keyLower}`] = new Date(value);
                  } catch (e: unknown) {
                    console.error('Invalid expires date in cookie:', value, e);
                  }
                } else if (keyLower === 'max-age') {
                  cookieOptions[`${keyLower}`] = parseInt(value, 10);
                } else {
                  cookieOptions[`${keyLower}`] = value;
                }
              } else {
                // Handle flags like HttpOnly, Secure, etc.
                const flagName = part.toLowerCase();
                if (flagName === 'secure') {
                  cookieOptions.secure = true;
                } else if (flagName === 'samesite') {
                  cookieOptions.sameSite = true;
                } else {
                  cookieOptions[`${flagName}`] = true;
                }
              }
            }

            // Ensure path is set
            if (!cookieOptions.path) {
              cookieOptions.path = '/';
            }

            setCookie(cookiesConstants.COOKIES.SESSION_TOKEN.name, tokenValue, cookieOptions);
          }
        });
      }
    }
  }, [session]);

  // Always provide a valid value to the context
  const contextValue = session
    ? {
        ...session,
        sessionToken: session.sessionToken || sessionToken || null,
        sessionId: session.sessionId || '',
      }
    : sessionToken
      ? {
          sessionToken: sessionToken,
        }
      : defaultContextValue;

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};

export const useSession = () => {
  const context = useContext(SessionContext);

  // Don't throw an error - return the default value instead
  // This prevents the hydration error
  return context || { sessionToken: null };
};
